import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { I18NextModule } from 'angular-i18next';
import { Areas, Departaments } from '../../model/project.model';
import { OrderService } from 'src/app/core/services/order.service';
import { MatSelectModule } from '@angular/material/select';
import { EsService } from 'src/app/core/services/es.service';
import { FeedbackModalComponent } from '../feedback-modal/feedback-modal.component';
import i18next from 'i18next';

@Component({
  standalone: true,
  selector: 'app-modal-create-driver',
  templateUrl: './modal-create-driver.component.html',
  styleUrls: ['./modal-create-driver.component.scss'],
  imports: [
    CommonModule,
    I18NextModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule
  ]
})
export class ModalCreateDriverComponent implements OnInit {
  form: FormGroup;
  departaments: Departaments[] = [];
  areas: Areas[] = [];
  loading = false;

  constructor(
    public dialogRef: MatDialogRef<ModalCreateDriverComponent>,
    private orderService: OrderService,
    private esService: EsService,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.form = new FormGroup({
      dni: new FormControl('', Validators.required),
      driverLicense: new FormControl('', Validators.required),
      name: new FormControl('', [
        Validators.required,
        Validators.maxLength(32)
      ]),
      lastName: new FormControl('', [
        Validators.required,
        Validators.maxLength(64)
      ]),
      company: new FormControl('', Validators.required),
      address: new FormControl('', [Validators.maxLength(64)]),
      area: new FormControl('', Validators.required),
      departament: new FormControl('', Validators.required),
      telephone: new FormControl('', [
        Validators.minLength(9),
        Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$')
      ]),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern('[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+[.]+[a-zA-Z]{2,4}$')
      ])
    });

    if (this.data) {
      const fullName = this.data.fullName || '';
      const [name, ...lastNameParts] = fullName.split(' ');
      const lastName = lastNameParts.join(' ');
      this.form.patchValue({
        dni: this.data.identificationDocument || '',
        driverLicense: this.data.driverLicenseDocument || '',
        name: name || '',
        lastName: lastName || '',
        company: this.data.company || '',
        address: this.data.address || '',
        departament: this.data.departamentCode || '',
        telephone: this.data.phoneNumber || '',
        email: this.data.email || ''
      });
      this.form.get('area')?.clearValidators();
      this.form.get('area')?.updateValueAndValidity();
    }
  }

  ngOnInit(): void {
    this.orderService.getProjectAreas().subscribe({
      next: (data: any) => {
        this.areas = data;
      },
      error: (error) => {
        console.error('Error fetching authorizers:', error);
      },
      complete: () => {
        console.log('Fetch complete');
      }
    });

    const areaSelected = this.form.get('area')?.value;

    if (!areaSelected) {
      this.form.get('departament')?.disable();
    } else {
      this.form.get('departament')?.enable();
    }
  }

  areaChange() {
    const areaSelected = this.form.get('area')?.value;

    this.orderService.getProjectDepartaments(areaSelected).subscribe({
      next: (data: any) => {
        this.departaments = data;
      },
      error: (error) => {
        console.error('Error fetching authorizers:', error);
      },
      complete: () => {
        console.log('Fetch complete');
      }
    });

    if (!areaSelected) {
      this.form.get('departament')?.disable();
    } else {
      this.form.get('departament')?.enable();
    }

    this.form.getRawValue();
  }

  submitRequest() {
    this.loading = true;
    const nullIfEmpty = (value: any) => (value === '' ? null : value);

    const company = nullIfEmpty(this.form.get('company')?.value);
    const departamentCode = nullIfEmpty(this.form.get('departament')?.value);
    const email = nullIfEmpty(this.form.get('email')?.value);
    const fullName = nullIfEmpty(
      `${this.form.get('name')?.value || ''} ${this.form.get('lastName')?.value || ''}`.trim()
    );
    const identificationNumber = nullIfEmpty(this.form.get('dni')?.value);
    const address = nullIfEmpty(this.form.get('address')?.value);
    const phoneNumber = nullIfEmpty(this.form.get('telephone')?.value);
    const driverLicense = nullIfEmpty(this.form.get('driverLicense')?.value);

    this.esService
      .createOrUpdateDriver(
        company,
        departamentCode,
        email,
        fullName,
        identificationNumber,
        address,
        phoneNumber,
        driverLicense,
        this.data ? this.data.id : null
      )
      .subscribe({
        next: () => {
          this.loading = false;
          this.dialog.open(FeedbackModalComponent, {
            width: '400px',
            data: {
              text: i18next.t('driverCreatedOK'),
              close: true,
              theme: 'light'
            }
          });
          this.dialogRef.close();
        },
        error: (e) => {
          this.loading = false;
          this.dialog.open(FeedbackModalComponent, {
            width: '400px',
            data: {
              text: e.error.message.includes(
                'Existing active external driver with code'
              )
                ? i18next.t('existingDriver')
                : i18next.t('driverCreatedKO'),
              close: true,
              theme: 'light'
            }
          });
        },
        complete: () => {
          this.loading = false;
          console.log('Fetch complete');
        }
      });
  }
}
